"use client";

import { useState, useEffect } from "react";
import { HelpCircle, ChevronDown, Mail, Phone } from "lucide-react";
import Link from "next/link";
import { useI18n } from "@/i18n/I18nProvider";
import PageHeader from "@/components/layout/PageHeader";
import EmptyState from "@/components/ui/EmptyState";
import { getFAQs } from "@/services/mockService";
import type { FAQItem } from "@/data/mockData";

const categories = ["All", "General", "Booking", "Payment", "Travel", "Privacy"];

export default function HelpPage() {
  const { t } = useI18n();
  const [faqs, setFaqs] = useState<FAQItem[]>([]);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState("All");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getFAQs(activeCategory === "All" ? undefined : activeCategory).then((data) => {
      if (!alive) return;
      setFaqs(data);
      setLoadedFor(activeCategory);
    });
    return () => { alive = false; };
  }, [activeCategory]);

  const loading = loadedFor !== activeCategory;

  return (
    <div className="pb-24">
      <PageHeader
        title={t("Help Center")}
        subtitle={t("Find answers to common questions about your care journey")}
      />

      {/* Categories */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar px-5 py-3 bg-white border-b border-[#EDF1F1]">
        {categories.map((cat) => (
          <button key={cat} onClick={() => { setActiveCategory(cat); setExpandedId(null); }}
            className={`cc-chip ${activeCategory === cat ? "cc-chip-active" : ""}`}>
            {t(cat)}
          </button>
        ))}
      </div>

      {/* FAQ List */}
      <div className="px-5 py-4 space-y-2.5">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <div key={i} className="cc-skeleton h-[68px]" />)
        ) : faqs.length === 0 ? (
          <EmptyState
            icon={HelpCircle}
            title={t("No FAQs in this category")}
            hint={t("Try another topic or chat with our concierge team.")}
          />
        ) : (
          faqs.map((faq) => {
            const isExpanded = expandedId === faq.id;
            return (
              <div key={faq.id} className="cc-card overflow-hidden">
                <button onClick={() => setExpandedId(isExpanded ? null : faq.id)}
                  className="w-full p-4 flex items-center gap-3 text-left active:bg-gray-50">
                  <HelpCircle size={17} className="text-primary shrink-0" />
                  <span className="text-[14.5px] font-semibold leading-[1.4] text-text-dark flex-1">{faq.question}</span>
                  <ChevronDown size={15} className={`text-text-dark/30 shrink-0 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                </button>
                {isExpanded && (
                  <div className="px-4 pb-4 pt-0">
                    <p className="text-[14px] text-text-dark/60 leading-[1.55] pl-[29px]">{faq.answer}</p>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Contact */}
      <div className="px-5 pb-4">
        <h2 className="cc-section-title mb-3">{t("Still need help?")}</h2>
        <div className="space-y-2.5">
          <Link href="/concierge" className="flex items-center gap-3 cc-card p-4 active:bg-gray-50">
            <Mail size={19} className="text-primary shrink-0" />
            <div className="flex-1">
              <p className="text-[14.5px] font-semibold text-text-dark">{t("Chat with Concierge")}</p>
              <p className="text-[12.5px] text-text-dark/50 mt-0.5">{t("Get help from our medical coordinators")}</p>
            </div>
          </Link>
          <div className="flex items-center gap-3 cc-card p-4">
            <Phone size={19} className="text-primary shrink-0" />
            <div className="flex-1">
              <p className="text-[14.5px] font-semibold text-text-dark">{t("Call Us")}</p>
              <p className="text-[12.5px] text-text-dark/50 mt-0.5">+86-400-123-4567 (Mon-Fri 9:00-18:00 CST)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Policies */}
      <div className="px-5 pb-4">
        <h2 className="cc-section-title mb-3">{t("Policies")}</h2>
        <div className="space-y-2.5">
          <div className="cc-card p-4">
            <p className="text-[14.5px] font-semibold text-text-dark mb-1.5">{t("Cancellation Policy")}</p>
            <p className="text-[13.5px] text-text-dark/55 leading-[1.55]">
              {t("Free cancellation before “Confirmed” status. After confirmation, hospital cancellation terms apply. Contact your concierge for assistance with cancellations.")}
            </p>
          </div>
          <div className="cc-card p-4">
            <p className="text-[14.5px] font-semibold text-text-dark mb-1.5">{t("Privacy Policy")}</p>
            <p className="text-[13.5px] text-text-dark/55 leading-[1.55]">
              {t("Your medical data is encrypted and never shared without explicit consent. We comply with international data protection standards. Request data deletion at any time.")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
